import { Loader } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { loadGoogleMaps } from "~/lib/google-maps";

interface MapPreviewProps {
	coordinates: string;
	className?: string;
}

export function MapPreview({ coordinates, className }: MapPreviewProps) {
	const mapRef = useRef<HTMLDivElement>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		// Skip if no coordinates or map element
		if (!coordinates || !mapRef.current) return;

		// Parse coordinates
		const [lat, lng] = coordinates.split(",").map(Number.parseFloat);
		if (Number.isNaN(lat) || Number.isNaN(lng)) {
			setError("Invalid coordinates format");
			setIsLoading(false);
			return;
		}

		// Initialize map when Google Maps is loaded
		const initMap = () => {
			if (!window.google?.maps) {
				setError("Google Maps failed to load");
				setIsLoading(false);
				return;
			}

			try {
				const mapOptions = {
					center: { lat, lng },
					zoom: 15,
					mapTypeId: window.google.maps.MapTypeId.ROADMAP,
					disableDefaultUI: true,
					zoomControl: true,
					styles: [
						{
							featureType: "all",
							elementType: "geometry",
							stylers: [{ color: "#242f3e" }],
						},
						{
							featureType: "all",
							elementType: "labels.text.stroke",
							stylers: [{ color: "#242f3e" }],
						},
						{
							featureType: "all",
							elementType: "labels.text.fill",
							stylers: [{ color: "#746855" }],
						},
						{
							featureType: "water",
							elementType: "geometry",
							stylers: [{ color: "#17263c" }],
						},
					],
				};

				// Create the map
				if (mapRef.current) {
					const map = new window.google.maps.Map(mapRef.current, mapOptions);

					// Try to use the AdvancedMarkerElement (preferred approach)
					try {
						if (
							window.google.maps.marker &&
							typeof window.google.maps.marker.AdvancedMarkerElement ===
								"function"
						) {
							// Use the new AdvancedMarkerElement
							new window.google.maps.marker.AdvancedMarkerElement({
								map,
								position: { lat, lng },
							});
						} else {
							throw new Error("AdvancedMarkerElement not available");
						}
					} catch (e) {
						// Fallback to the old Marker if AdvancedMarkerElement is not available
						new window.google.maps.Marker({
							position: { lat, lng },
							map,
							// Don't use animation to avoid flickering
							// animation: window.google.maps.Animation.DROP
						});
					}
				}

				setIsLoading(false);
			} catch (e) {
				setError("Error initializing map");
				setIsLoading(false);
			}
		};

		// Load Google Maps and initialize map
		loadGoogleMaps()
			.then(() => {
				initMap();
			})
			.catch(() => {
				setError("Failed to load Google Maps");
				setIsLoading(false);
			});

		return () => {
			// Cleanup
		};
	}, [coordinates]);

	return (
		<div className={`relative rounded overflow-hidden ${className || ""}`}>
			<div
				ref={mapRef}
				className="w-full h-full min-h-[180px] md:min-h-[250px]"
			/>

			{isLoading && (
				<div className="absolute inset-0 flex items-center justify-center bg-primary/20">
					<Loader className="w-6 h-6 text-white animate-spin" />
				</div>
			)}

			{error && (
				<div className="absolute inset-0 flex items-center justify-center bg-primary/20">
					<p className="text-sm text-white/70">{error}</p>
				</div>
			)}
		</div>
	);
}

// Add TypeScript definitions for Google Maps
declare global {
	interface Window {
		google?: {
			maps: {
				Map: new (
					container: HTMLElement,
					options: GoogleMapOptions,
				) => GoogleMap;
				Marker: new (options: MarkerOptions) => GoogleMarker;
				marker?: {
					AdvancedMarkerElement: new (
						options: AdvancedMarkerOptions,
					) => unknown;
				};
				Animation: {
					DROP: number;
					BOUNCE: number;
				};
				MapTypeId: {
					ROADMAP: string;
					SATELLITE: string;
					HYBRID: string;
					TERRAIN: string;
				};
			};
		};
	}
}

interface GoogleMapOptions {
	center: { lat: number; lng: number };
	zoom: number;
	mapTypeId: string;
	disableDefaultUI?: boolean;
	zoomControl?: boolean;
	styles?: Array<{
		featureType: string;
		elementType: string;
		stylers: Array<Record<string, string | number | boolean>>;
	}>;
}

interface MarkerOptions {
	position: { lat: number; lng: number };
	map: GoogleMap;
	animation?: number;
}

// Minimal type definitions
type GoogleMap = unknown;
type GoogleMarker = unknown;

// Add new interface for AdvancedMarkerOptions
interface AdvancedMarkerOptions {
	position: { lat: number; lng: number };
	map: GoogleMap;
}
